import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, formatApiError } from "@/context/AuthContext";

const CONSENT_ITEMS = [
  {
    key: "terms",
    testid: "consent-terms",
    label: (
      <>
        <Link to="/kullanim" target="_blank" className="font-semibold underline hover:text-zinc-900">
          Kullanım Şartları
        </Link>
        'nı okudum ve kabul ediyorum.
      </>
    ),
  },
  {
    key: "privacy",
    testid: "consent-privacy",
    label: (
      <>
        <Link to="/gizlilik" target="_blank" className="font-semibold underline hover:text-zinc-900">
          Gizlilik Politikası
        </Link>
        'nı okudum ve kabul ediyorum.
      </>
    ),
  },
  {
    key: "kvkk",
    testid: "consent-kvkk",
    label: (
      <>
        <Link to="/aydinlatma" target="_blank" className="font-semibold underline hover:text-zinc-900">
          KVKK Aydınlatma Metni
        </Link>
        'ni okudum ve kişisel verilerimin işlenmesini kabul ediyorum.
      </>
    ),
  },
  {
    key: "cookie",
    testid: "consent-cookie",
    label: (
      <>
        <Link to="/cerez" target="_blank" className="font-semibold underline hover:text-zinc-900">
          Çerez Politikası
        </Link>
        'nı okudum ve zorunlu çerezlerin kullanımını onaylıyorum.
      </>
    ),
  },
];

export default function RegisterPage() {
  const { user, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    kvkk: false,
    cookie: false,
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const allAccepted =
    consents.terms && consents.privacy && consents.kvkk && consents.cookie;

  const toggle = (key) => (checked) =>
    setConsents((c) => ({ ...c, [key]: !!checked }));

  const acceptAll = () =>
    setConsents({ terms: true, privacy: true, kvkk: true, cookie: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAccepted) {
      toast.error("Devam etmek için tüm onayları vermeniz gerekir.");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, consents);
      toast.success("Hesap oluşturuldu");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1635111057505-3b7dcc2b72fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwcHJvcGVydHklMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODUwMzM1NTh8MA&ixlib=rb-4.1.0&q=85"
          alt="Ekspertiz"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-900/40 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-white text-zinc-900 rounded-sm">
              <MapPin className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <span className="text-xl tracking-tight font-semibold">Kolay Arşivim</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Ekspertiz kaydını<br /> saniyeler içinde başlat.
            </h1>
            <p className="mt-6 text-base text-zinc-200 max-w-md">
              Ücretsiz hesap oluşturun; ada/parsel, taşınmaz türü, konum ve notlarınızla ekspertiz kayıtlarınızı düzenli tutun.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-6">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900">Kayıt Ol</h2>
            <p className="mt-2 text-sm text-zinc-500">Hemen kaydolun ve kullanmaya başlayın.</p>
          </div>
          <form className="space-y-4" onSubmit={handleSubmit} data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Ad Soyad</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                placeholder="Adınız Soyadınız"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                placeholder="ornek@parsel.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                placeholder="En az 8 karakter"
              />
            </div>

            {/* KVKK / yasal onaylar */}
            <div className="pt-2 border-t border-zinc-200">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                  Yasal Onaylar
                </div>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 underline underline-offset-2"
                  data-testid="consent-accept-all-btn"
                >
                  Tümünü kabul et
                </button>
              </div>
              <div className="space-y-2.5">
                {CONSENT_ITEMS.map((item) => (
                  <label
                    key={item.key}
                    htmlFor={item.testid}
                    className="flex items-start gap-2.5 cursor-pointer"
                  >
                    <Checkbox
                      id={item.testid}
                      checked={consents[item.key]}
                      onCheckedChange={toggle(item.key)}
                      data-testid={`${item.testid}-checkbox`}
                      className="mt-0.5"
                    />
                    <span className="text-[12px] text-zinc-600 leading-snug">
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !allAccepted}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : allAccepted ? (
                "Kayıt Ol"
              ) : (
                "Onayları vererek devam edin"
              )}
            </Button>
          </form>
          <p className="mt-6 text-sm text-zinc-500">
            Hesabınız var mı?{" "}
            <Link to="/giris" className="text-zinc-900 font-medium underline underline-offset-4" data-testid="go-login-link">
              Giriş yapın
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
